/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { v4 } from "uuid";
import { Box, Heading, Loader, Spacer, Text } from "@nordcloud/gnui";
import { QueryPagination } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { ServiceGroupsAdd } from "~/components/ServiceGroups/ServiceGroupsAdd";
import { ServiceGroupItem } from "~/components/ServiceGroups/ServiceGroupsItem";
import { ServiceGroupsAddId } from "~/components/ServiceGroups/types";
import { ACTIONS, Can } from "~/routing";
import { useAxios, useQueryState } from "~/tools";

function ServiceGroupsPage() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [serviceGroups, setServiceGroups] = React.useState({
    list: [],
    totalCount: 0,
  });

  const [{ limit, page }] = useQueryState();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchServiceGroup = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/catalog/serviceGroups?limit=${limit}&page=${page}`
      );
      setServiceGroups({
        list: data?.serviceGroups,
        totalCount: data?.totalCount,
      });
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, limit, page, showError]);

  React.useEffect(() => {
    fetchServiceGroup();
  }, [fetchServiceGroup]);

  const serviceGroupsAddId = serviceGroups?.list?.map((serviceGroup) => ({
    idV4: v4(),
    serviceGroup,
  }));

  return (
    <>
      <Box css={{ display: "flex", alignItems: "center" }} spacing="spacing04">
        <Heading level={2}>Service Groups</Heading>
        <Can action={ACTIONS.ADD_SERVICE_GROUP}>
          <ServiceGroupsAdd refetch={fetchServiceGroup} />
        </Can>
      </Box>
      <Box spacing="spacing04">
        {isLoading && <Loader inContent />}
        {serviceGroupsAddId && !isLoading ? (
          serviceGroupsAddId.map((s: ServiceGroupsAddId) => (
            <ServiceGroupItem
              key={s.idV4}
              serviceGroup={s.serviceGroup}
              refetch={fetchServiceGroup}
            />
          ))
        ) : (
          <>
            <Spacer height="1rem" />
            <Text>No data available.</Text>
          </>
        )}
        <QueryPagination count={Number(serviceGroups?.totalCount) || 0} />
      </Box>
    </>
  );
}

export { ServiceGroupsPage };
